import { Box } from '@mui/material';
import { forwardRef } from 'react';
import { Barcode as BarcodeUtils } from 'shared/core';
import { asUTCDate, fromUTC } from 'shared/utils/DateTime';
import type { Lot, PalletTag as PalletTagRow, ProductionRunForPalletTags } from 'src/modules/palletTags/types';
import { basicTemplate, PalletTag } from './PalletTag';

const oneOrMixed = <T,>(arr: readonly T[] | null | undefined, format: (value: T) => unknown = (value) => value) => {
  if (!arr?.length) {
    return '';
  }

  return arr.length === 1 ? format(arr[0]) || '' : 'Mixed';
};

const formatDate = (date: ReturnType<typeof asUTCDate>) => asUTCDate(date)?.toFormat('MM/dd/yy') ?? '--';

const formatMonthDay = (date: ReturnType<typeof asUTCDate>) => asUTCDate(date)?.toFormat('LLLdd') ?? '--';

export const PalletTagsPreview = forwardRef<
  HTMLDivElement,
  { palletTags: PalletTagRow[]; lot?: Lot; lots?: Lot[]; productionRun?: ProductionRunForPalletTags }
>(({ palletTags, lot, lots, productionRun }, ref) => {
  const productionRunRow = productionRun && 'slug' in productionRun ? productionRun : undefined;
  return (
    <Box ref={ref}>
      {palletTags.map((palletTag, i) => {
        const tagLot = lot ?? lots?.find((lot) => lot.id === palletTag.lotId);
        if (!tagLot) throw new TypeError("Cannot read properties of undefined (reading 'product')");
        const product = tagLot.product;
        const template = palletTag.tagLayout?.schema ?? basicTemplate;
        const count = palletTag.tagLayout?.tagType === 'CASE' ? palletTag.cartonQuantity : 1;

        const grower = oneOrMixed(
          tagLot.lotSupplierSources?.filter((source) => source?.type === 'GROWER'),
          (source) => source?.businessEntity?.name
        );

        const vendor = oneOrMixed(
          tagLot.lotSupplierSources?.filter((source) => source?.type === 'VENDOR'),
          (source) => source?.businessEntity?.name
        );

        const growerOrVendor = oneOrMixed(
          tagLot.lotSupplierSources?.filter((source) => source?.type === 'GROWER' || source?.type === 'VENDOR'),
          (source) => source?.businessEntity?.name
        );

        const fieldTicketId = oneOrMixed(tagLot.fieldTicketIds);
        const harvestLocation = oneOrMixed(tagLot.harvestLocations);
        // `||` not `??`: Lot.weightPerInventoryUnit computes 0 for depleted lots — must fall through to product case weight (TAS-3385)
        const perInventoryUnitWeight = tagLot?.weightPerInventoryUnit || product.weightPerInventoryUnit;
        // Created with 0 units = a weight-only case label: with no recorded weight it prints one case weight; a depleted pallet keeps 0 (TAS-3385)
        const isWeightOnlyCaseLabel = !palletTag.initialQuantity && !palletTag.cartonQuantity && !palletTag.weight;
        const weight = isWeightOnlyCaseLabel
          ? perInventoryUnitWeight
          : (palletTag.weight ?? perInventoryUnitWeight * tagLot.quantity);
        const weightPerUnit =
          palletTag.weight && palletTag.cartonQuantity
            ? palletTag.weight / palletTag.cartonQuantity
            : perInventoryUnitWeight;

        // Date Only
        const receiveDate = asUTCDate(tagLot.receiveDate);

        // Date time
        const repackStartDate = fromUTC(productionRunRow?.startedAt);
        const repackCompletedDate = fromUTC(productionRunRow?.completedAt);

        const repackDate = repackCompletedDate || repackStartDate;
        const date = repackDate || receiveDate;

        const pickCode = BarcodeUtils.voicePickCode(
          // `product` (from tagLot, above) is the GTIN the tag actually renders at `gtinCode`
          // below. `palletTag.product` is not selected by any PalletTag query — the hand-rolled
          // type just lets it compile — so reading it hashed the literal string "undefined".
          String(product.gtinCode),
          tagLot.slug,
          date?.toFormat('yyMMdd') ?? ''
        );

        return (
          <>
            {Array.from({ length: count }).map((_, j) => (
              <PalletTag
                key={`${palletTag.id}-${j}`}
                template={template}
                id={`tag-${i}-${j}`}
                data={{
                  // System attributes

                  lot: tagLot.slug,
                  productionRunSlug: productionRunRow?.slug || '',
                  tagNumber: palletTag.tagNumber,

                  // Numeric attributes

                  quantity: palletTag.cartonQuantity,

                  weight:
                    typeof weight === 'number'
                      ? weight.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                      : weight,
                  weightPerUnit:
                    typeof weightPerUnit === 'number'
                      ? weightPerUnit.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                      : weightPerUnit,

                  // Product attributes

                  description: product.organic
                    ? `Organic ${product.commodity.name} ${product.commodityStyle.name}`
                    : `${product.commodity.name} ${product.commodityStyle.name}`,
                  packStyle: `${product.commodityUnit.name} ${product.commoditySize.name}`,
                  grade: product.grade,
                  label: product.commodityLabel?.name || '',
                  size: product.commoditySize?.name || '',
                  unit: product.commodityUnit?.name || '',
                  commodity: product.commodity?.name || '',
                  variety: product.commodityStyle?.name || '',
                  countryOfOrigin: product.countryOfOrigin,
                  organic: product.organic ? 'Organic' : '',
                  customProductFields: product.customFields,

                  // Source attributes

                  grower,
                  vendor,
                  growerOrVendor,
                  fieldTicketId,
                  harvestLocation,

                  // Dates

                  receiveDate: formatDate(receiveDate),
                  repackStartDate: formatDate(repackStartDate),
                  repackCompletedDate: formatDate(repackCompletedDate),
                  date: formatDate(date),
                  receiveDateMonthDay: formatMonthDay(receiveDate),
                  repackStartDateMonthDay: formatMonthDay(repackStartDate),
                  repackCompletedDateMonthDay: formatMonthDay(repackCompletedDate),
                  dateMonthDay: formatMonthDay(date),
                  rawDate: date,
                  pickCode,
                  gtinCode: product.gtinCode,
                  productCode: product.productCode,
                  unitsPerPallet: product.cartonsPerPallet,
                }}
              />
            ))}
          </>
        );
      })}
    </Box>
  );
});
